import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  ProviderParameterValue,
  VideoGenerationMode,
  VideoJob,
  VideoProject,
  assignVideoJobsToProject,
  createVideoProject,
  deleteVideoProject,
  exportProjectVideo,
  onUserVideoJobsSnapshot,
  onUserVideoProjectsSnapshot,
  pollVideo,
  submitVideo,
  updateVideoProject,
} from "../firebase/video";
import { onCreditsSnapshot, DebateCredits } from "../firebase/credits";
import VideoConfigForm from "./VideoConfigForm";
import VideoGallery from "./VideoGallery";
import BuyChatCreditsModal from "./BuyChatCreditsModal";
import { hasFreeAccess } from "../utils/freeAccess";
import WorkspaceNavigation, { Brand } from "./WorkspaceNavigation";
import SettingsButton from "./SettingsButton";
import Dialog from "./Dialog";
import SlidingIndicator from "./SlidingIndicator";
import ActionButton, { useActionStatus } from "./ActionButton";
import {
  Film,
  Sparkles,
  Folder,
  FolderPlus,
  Loader2,
  Plus,
  Inbox,
  Trash2,
  Wallet,
  X,
} from "lucide-react";

const POLL_INTERVAL_MS = 30_000;
const MAX_POLL_RETRIES = 3;

type ProjectWithId = VideoProject & { id: string };
type JobWithId = VideoJob & { id: string };

function normalizeJobProjectId(job: Pick<VideoJob, "projectId">): string | null {
  const projectId = job.projectId;
  return typeof projectId === "string" && projectId.trim() ? projectId : null;
}

const VideoGenerator: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const uid = currentUser?.uid ?? "";
  const email = currentUser?.email ?? "";
  const freeAccess = hasFreeAccess(email);

  const [jobs, setJobs] = useState<JobWithId[]>([]);
  const [jobsLoaded, setJobsLoaded] = useState(false);
  const [projects, setProjects] = useState<ProjectWithId[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [newProjectTitle, setNewProjectTitle] = useState("");
  const [creatingProject, setCreatingProject] = useState(false);
  const createAction = useActionStatus();
  const [moveTargetProjectId, setMoveTargetProjectId] = useState("");
  const [movingUnassigned, setMovingUnassigned] = useState(false);
  const [renamingTitle, setRenamingTitle] = useState("");
  const [projectPendingDelete, setProjectPendingDelete] =
    useState<ProjectWithId | null>(null);
  const [deletingProject, setDeletingProject] = useState(false);
  const [credits, setCredits] = useState<DebateCredits | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [exportingProjectId, setExportingProjectId] = useState<string | null>(
    null,
  );
  const [showBuyCredits, setShowBuyCredits] = useState(false);
  const [studioPanel, setStudioPanel] = useState<"create" | "library" | "projects">("create");
  const createRef = useRef<HTMLElement>(null);
  const openCreate = useCallback(() => {
    setStudioPanel("create");
    requestAnimationFrame(() => {
      createRef.current?.scrollTo({ top: 0 });
      createRef.current?.querySelector<HTMLTextAreaElement>("textarea")?.focus();
    });
  }, []);
  const [retryParams, setRetryParams] = useState<{
    model: string;
    prompt: string;
    generationMode?: VideoGenerationMode;
    duration: number | null;
    resolution: string | null;
    aspectRatio: string | null;
    size?: string | null;
    generateAudio: boolean;
    seed?: number | null;
    providerParameters?: Record<string, ProviderParameterValue>;
    sceneTitle?: string | null;
  } | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error";
  } | null>(null);

  const pollIntervals = useRef<Map<string, ReturnType<typeof setInterval>>>(
    new Map(),
  );
  const pollRetryCounts = useRef<Map<string, number>>(new Map());
  useEffect(() => {
    if (!uid) return;
    const unsub = onUserVideoProjectsSnapshot(
      uid,
      (nextProjects) => {
        setProjects(nextProjects);
        setProjectsLoading(false);
      },
      (err) => {
        console.error("Video projects snapshot error:", err);
        setProjectsLoading(false);
      },
    );
    return () => unsub();
  }, [uid]);

  useEffect(() => {
    if (
      selectedProjectId &&
      !projects.some((project) => project.id === selectedProjectId)
    ) {
      setSelectedProjectId(null);
    }
  }, [projects, selectedProjectId]);

  useEffect(() => {
    const selectedProject = projects.find(
      (project) => project.id === selectedProjectId,
    );
    setRenamingTitle(selectedProject?.title ?? "");
  }, [projects, selectedProjectId]);

  useEffect(() => {
    setMoveTargetProjectId((current) =>
      projects.some((project) => project.id === current)
        ? current
        : projects[0]?.id ?? "",
    );
  }, [projects]);

  useEffect(() => {
    if (
      selectedProjectId &&
      projects.some((project) => project.id === selectedProjectId)
    ) {
      setMoveTargetProjectId(selectedProjectId);
    }
  }, [projects, selectedProjectId]);

  useEffect(() => {
    if (!uid) return;
    const unsub = onUserVideoJobsSnapshot(uid, (next) => { setJobs(next); setJobsLoaded(true); }, (err) => {
      setJobsLoaded(true);
      console.error("Video jobs snapshot error:", err);
      if (err.message?.includes("index")) {
        console.error(
          "A Firestore composite index is required. Check the error above for a link to create it.",
        );
      }
    });
    return () => unsub();
  }, [uid]);

  useEffect(() => {
    if (!uid) return;
    const unsub = onCreditsSnapshot(uid, setCredits);
    return () => unsub();
  }, [uid]);

  useEffect(() => {
    const activeJobs = jobs.filter(
      (job) => job.status === "pending" || job.status === "in_progress",
    );
    const activeIds = new Set(activeJobs.map((job) => job.id));

    Array.from(pollIntervals.current.entries()).forEach(([jobId, interval]) => {
      if (!activeIds.has(jobId)) {
        clearInterval(interval);
        pollIntervals.current.delete(jobId);
        pollRetryCounts.current.delete(jobId);
      }
    });

    for (const job of activeJobs) {
      if (!pollIntervals.current.has(job.id)) {
        const interval = setInterval(async () => {
          try {
            await pollVideo(job.id, uid);
            pollRetryCounts.current.set(job.id, 0);
          } catch {
            const count = (pollRetryCounts.current.get(job.id) ?? 0) + 1;
            pollRetryCounts.current.set(job.id, count);
            if (count > MAX_POLL_RETRIES) {
              console.warn(`Poll retry count exceeded for job ${job.id}`);
            }
          }
        }, POLL_INTERVAL_MS);
        pollIntervals.current.set(job.id, interval);
        pollVideo(job.id, uid).catch(() => {});
      }
    }
  }, [jobs, uid]);

  useEffect(() => {
    const intervals = pollIntervals.current;
    return () => {
      Array.from(intervals.values()).forEach((interval) => {
        clearInterval(interval);
      });
    };
  }, []);

  const selectedProject = useMemo(
    () =>
      selectedProjectId
        ? projects.find((project) => project.id === selectedProjectId) ?? null
        : ({
            id: "",
            uid,
            title: "Unassigned videos",
            description: "Clips not grouped into a project.",
            totalJobs: 0,
            completedJobs: 0,
            exportStatus: null,
            exportedVideoUrl: null,
            exportedFileName: null,
            createdAt: null,
            updatedAt: null,
          } as ProjectWithId),
    [projects, selectedProjectId, uid],
  );

  const selectedProjectJobs = useMemo(
    () =>
      jobs
        .filter((job) => normalizeJobProjectId(job) === selectedProjectId)
        .sort((a, b) => {
          const aIndex = typeof a.sceneIndex === "number" ? a.sceneIndex : 0;
          const bIndex = typeof b.sceneIndex === "number" ? b.sceneIndex : 0;
          return aIndex - bIndex;
        }),
    [jobs, selectedProjectId],
  );

  const unassignedJobs = useMemo(
    () => jobs.filter((job) => normalizeJobProjectId(job) === null),
    [jobs],
  );
  const unassignedJobCount = unassignedJobs.length;

  const activeJobCount = jobs.filter(
    (job) => job.status === "pending" || job.status === "in_progress",
  ).length;

  const handleSubmit = useCallback(
    async (params: {
      projectId: string | null;
      sceneTitle: string | null;
      sceneIndex: number;
      model: string;
      prompt: string;
      generationMode: VideoGenerationMode;
      duration: number;
      resolution: string | null;
      aspectRatio: string | null;
      size: string | null;
      generateAudio: boolean;
      seed: number | null;
      frameImages: Array<{ frameType: "first_frame" | "last_frame"; url: string }>;
      inputReferences: Array<{ url: string }>;
      providerParameters: Record<string, ProviderParameterValue>;
    }) => {
      setSubmitting(true);
      try {
        const result = await submitVideo({
          uid,
          email,
          ...params,
        });
        setRetryParams(null);
        setStudioPanel("library");
        setToast({
          message: "Clip submitted. Generation is in progress.",
          type: "success",
        });
        pollVideo(result.jobId, uid).catch(() => {});
      } catch (err: any) {
        if (err.message?.toLowerCase().includes("insufficient")) {
          setShowBuyCredits(true);
        } else {
          setToast({
            type: "error",
            message: err.message || "Failed to submit video generation.",
          });
        }
        throw err;
      } finally {
        setSubmitting(false);
      }
    },
    [uid, email],
  );

  const handleRetry = useCallback((job: VideoJob) => {
    openCreate();
    setRetryParams({
      model: job.model,
      prompt: job.prompt,
      generationMode: job.generationMode ?? "text_to_video",
      duration: job.duration,
      resolution: job.resolution,
      aspectRatio: job.aspectRatio,
      size: job.size,
      generateAudio: job.generateAudio,
      seed: job.seed,
      providerParameters: job.providerParameters,
      sceneTitle: job.sceneTitle,
    });
  }, [openCreate]);

  const handleCreateProject = useCallback(async () => {
    if (!uid || creatingProject) return false;
    setCreatingProject(true);
    try {
      const title =
        newProjectTitle.trim() || `Video project ${projects.length + 1}`;
      const projectId = await createVideoProject(uid, title);
      const optimisticProject: ProjectWithId = {
        id: projectId,
        uid,
        title,
        description: "",
        totalJobs: 0,
        completedJobs: 0,
        exportStatus: null,
        exportedVideoUrl: null,
        exportedFileName: null,
        exportWarning: null,
        exportError: null,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      };
      setProjects((current) => [
        optimisticProject,
        ...current.filter((project) => project.id !== projectId),
      ]);
      setMoveTargetProjectId(projectId);
      setSelectedProjectId(unassignedJobCount > 0 ? null : projectId);
      setNewProjectTitle("");
      setToast({
        type: "success",
        message:
          unassignedJobCount > 0
            ? "Project created. You can move unassigned clips into it now."
            : "Project created.",
      });
      return true;
    } catch (err: any) {
      setToast({
        type: "error",
        message: err.message || "Failed to create project.",
      });
      return false;
    } finally {
      setCreatingProject(false);
    }
  }, [
    uid,
    creatingProject,
    newProjectTitle,
    projects.length,
    unassignedJobCount,
  ]);

  const handleRenameProject = useCallback(async () => {
    if (!selectedProjectId || !selectedProject || !renamingTitle.trim()) return;
    try {
      await updateVideoProject(selectedProject.id, {
        title: renamingTitle.trim(),
      });
      setToast({ type: "success", message: "Project renamed." });
    } catch (err: any) {
      setToast({
        type: "error",
        message: err.message || "Failed to rename project.",
      });
    }
  }, [selectedProjectId, selectedProject, renamingTitle]);

  const handleMoveUnassignedToProject = useCallback(async () => {
    if (
      !uid ||
      !moveTargetProjectId ||
      unassignedJobs.length === 0
    ) {
      return;
    }

    setMovingUnassigned(true);
    try {
      const targetProject = projects.find(
        (project) => project.id === moveTargetProjectId,
      );
      await assignVideoJobsToProject({
        uid,
        projectId: moveTargetProjectId,
        jobIds: unassignedJobs.map((job) => job.id),
      });
      setSelectedProjectId(moveTargetProjectId);
      setToast({
        type: "success",
        message: `Moved ${unassignedJobs.length} ${
          unassignedJobs.length === 1 ? "clip" : "clips"
        } to ${targetProject?.title ?? "project"}.`,
      });
    } catch (err: any) {
      setToast({
        type: "error",
        message: err.message || "Failed to move videos.",
      });
    } finally {
      setMovingUnassigned(false);
    }
  }, [
    uid,
    moveTargetProjectId,
    unassignedJobs,
    projects,
  ]);

  const handleDeleteProject = useCallback(async () => {
    if (!uid || !projectPendingDelete || deletingProject) return;

    setDeletingProject(true);
    try {
      const result = await deleteVideoProject({
        uid,
        projectId: projectPendingDelete.id,
      });
      setProjects((current) =>
        current.filter((project) => project.id !== projectPendingDelete.id),
      );
      setSelectedProjectId(null);
      setProjectPendingDelete(null);
      setToast({
        type: "success",
        message: result.unassigned
          ? `Project deleted. ${result.unassigned} ${
              result.unassigned === 1 ? "clip is" : "clips are"
            } now unassigned.`
          : "Project deleted.",
      });
    } catch (err: any) {
      setToast({
        type: "error",
        message: err.message || "Failed to delete project.",
      });
    } finally {
      setDeletingProject(false);
    }
  }, [uid, projectPendingDelete, deletingProject]);

  const handleExportProject = useCallback(async () => {
    if (!uid || !selectedProjectId || !selectedProject) {
      setToast({
        type: "error",
        message: "Select or create a project before exporting an MP4.",
      });
      return false;
    }
    const completedIds = selectedProjectJobs
      .filter((job) => job.status === "completed" && job.videoUrl)
      .map((job) => job.id);
    if (!completedIds.length) {
      setToast({ type: "error", message: "No completed clips to export yet." });
      return false;
    }

    setExportingProjectId(selectedProject.id);
    try {
      const result = await exportProjectVideo({
        uid,
        projectId: selectedProjectId,
        clipIds: completedIds,
      });
      setToast({
        type: "success",
        message: result.warning || "Project video exported.",
      });
      return true;
    } catch (err: any) {
      setToast({
        type: "error",
        message: err.message || "Failed to export project.",
      });
      return false;
    } finally {
      setExportingProjectId(null);
    }
  }, [uid, selectedProjectId, selectedProject, selectedProjectJobs]);

  const chatCredits = credits?.chatCredits ?? 0;

  useEffect(() => {
    if (!toast) return;
    const timeout = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(timeout);
  }, [toast]);

  return (
    <div className="app-frame video-studio">
      <a href="#video-workspace" className="skip-link">Skip to video workspace</a>
      {toast && (
        <div className="studio-toast" role={toast.type === "error" ? "alert" : "status"}>
          <div
            className={`flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${
              toast.type === "success"
                ? "bg-success-hover text-on-solid"
                : "bg-danger-hover text-on-solid"
            }`}
          >
            <span>{toast.message}</span>
            <button
              onClick={() => setToast(null)}
              className="ml-1 rounded-md p-0.5 text-on-solid hover:bg-surface/15"
              title="Dismiss"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      <header className="workspace-header">
        <Brand />
        <WorkspaceNavigation active="video" onChange={mode => { if (mode !== "video") navigate(mode === "chat" ? "/?mode=chat" : mode === "feed" ? "/feed" : "/"); }} />
        <div className="header-actions"><SettingsButton />{freeAccess ? <span className="usage-button studio-balance cursor-default hover:bg-transparent" role="status"><Wallet size={16} aria-hidden="true" /><span>Free access</span></span> : <button type="button" className="usage-button studio-balance" onClick={() => setShowBuyCredits(true)} aria-label={`Video credit balance $${chatCredits.toFixed(2)}. Add credits`}><Wallet size={16} aria-hidden="true" /><span className="font-mono tabular-nums">${chatCredits.toFixed(2)}</span></button>}</div>
      </header>
      <nav className="studio-panel-switch" aria-label="Video studio panels">
        <SlidingIndicator selector='[aria-pressed="true"]' activeKey={studioPanel} className="seg-indicator-paper studio-panel-indicator" />
        {([['create', 'Create', Sparkles], ['library', 'Storyboard', Film], ['projects', 'Projects', Folder]] as const).map(([panel, label, Icon]) => <button type="button" key={panel} aria-pressed={studioPanel === panel} onClick={() => setStudioPanel(panel)}><Icon size={16} />{label}{panel === 'library' && activeJobCount > 0 && <span>{activeJobCount}</span>}</button>)}
      </nav>
      <main id="video-workspace" className="studio-workspace" data-panel={studioPanel}>
          <h1 className="sr-only">Video Studio</h1>
          <aside className="bubble-rail studio-projects" aria-label="Video projects">

            <div className="studio-project-card">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <Folder size={16} className="text-muted" />
                  Projects
                </div>
              </div>

              <div className="flex gap-2 mb-3">
                <input
                  aria-label="New project title"
                  value={newProjectTitle}
                  onChange={(e) => setNewProjectTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") createAction.run(handleCreateProject).catch(() => {});
                  }}
                  placeholder="New project"
                  className="min-w-0 flex-1 rounded-lg border border-line bg-subtle px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-focus"
                />
                <ActionButton
                  iconOnly
                  status={creatingProject ? "pending" : createAction.status}
                  onClick={() => { createAction.run(handleCreateProject).catch(() => {}); }}
                  icon={<Plus size={16} />}
                  label="Create project"
                  pendingLabel="Creating project"
                  successLabel="Project created"
                  errorLabel="Couldn't create project"
                  className="h-9 w-9 rounded-md bg-action text-on-solid hover:bg-action-hover active:translate-y-px disabled:opacity-50 transition-[background-color,transform] duration-150 shadow-xs"
                  title="Create project"
                />
              </div>

              <div className="space-y-1 max-h-[420px] overflow-y-auto">
                {projectsLoading ? (
                  <div role="status" aria-label="Loading projects" className="space-y-1">
                    {[70, 52, 61].map((w, i) => (
                      <div key={i} className="px-[13px] py-[13px]" aria-hidden="true">
                        <span className="skeleton skeleton-on-dark block h-3" style={{ width: `${w}%` }} />
                        <span className="skeleton skeleton-on-dark block h-2 w-1/3 mt-2" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <>
                    <button
                      type="button"
                      aria-current={selectedProjectId === null ? "true" : undefined}
                      onClick={() => { setSelectedProjectId(null); setStudioPanel("library"); }}
                      className={`studio-project-item ${
                        selectedProjectId === null
                          ? "bg-action text-on-solid shadow-xs font-semibold"
                          : "hover:bg-raised text-content border border-transparent"
                      }`}
                    >
                      <div className="flex min-w-0 items-center gap-2">
                        <Inbox size={15} className="flex-shrink-0 text-muted" />
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium">
                            Unassigned videos
                          </div>
                          <div className="text-xs text-muted">
                            {unassignedJobCount}{" "}
                            {unassignedJobCount === 1 ? "clip" : "clips"}
                          </div>
                        </div>
                      </div>
                    </button>

                    {projects.length === 0 ? (
                      <div className="rounded-lg border border-dashed border-control p-4 text-center text-sm text-muted">
                        <FolderPlus className="mx-auto mb-2 h-5 w-5 text-muted" />
                        Create a project when you want to group clips.
                      </div>
                    ) : (
                      projects.map((project) => {
                        const count = jobs.filter(
                          (job) => normalizeJobProjectId(job) === project.id,
                        ).length;
                        const selected = project.id === selectedProjectId;
                        return (
                          <button
                            key={project.id}
                            type="button"
                            aria-current={selected ? "true" : undefined}
                            onClick={() => { setSelectedProjectId(project.id); setStudioPanel("library"); }}
                            className={`studio-project-item ${
                              selected
                                ? "bg-action text-on-solid shadow-xs font-semibold"
                                : "hover:bg-raised text-content border border-transparent"
                            }`}
                          >
                            <div className="truncate text-sm font-medium">
                              {project.title}
                            </div>
                            <div className="text-xs text-muted">
                              {count} {count === 1 ? "clip" : "clips"}
                            </div>
                          </button>
                        );
                      })
                    )}
                  </>
                )}
              </div>
            </div>

            {unassignedJobCount > 0 && (
              <div className="studio-project-card">
                <label className="block text-xs font-semibold text-content mb-1">
                  Assign unassigned clips
                </label>
                <p className="mb-2 text-xs text-muted">
                  Move {unassignedJobCount}{" "}
                  {unassignedJobCount === 1 ? "clip" : "clips"} into a project
                  for storyboarding and MP4 export.
                </p>
                {projects.length > 0 ? (
                  <div className="space-y-2">
                    <select
                      aria-label="Move clips to project"
                      value={moveTargetProjectId}
                      onChange={(e) => setMoveTargetProjectId(e.target.value)}
                      className="w-full rounded-lg border border-line bg-subtle px-2 py-1.5 text-sm text-content focus:outline-none focus:ring-2 focus:ring-focus"
                    >
                      {projects.map((project) => (
                        <option key={project.id} value={project.id}>
                          {project.title}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={handleMoveUnassignedToProject}
                      disabled={!moveTargetProjectId || movingUnassigned}
                      className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-media px-3 py-2 text-sm font-medium text-on-solid hover:bg-media disabled:opacity-50"
                    >
                      {movingUnassigned && (
                        <Loader2 size={15} className="animate-spin" />
                      )}
                      Move {unassignedJobCount}{" "}
                      {unassignedJobCount === 1 ? "clip" : "clips"}
                    </button>
                  </div>
                ) : (
                  <p className="text-sm text-muted">
                    Create a project above when you want these clips grouped for
                    a stitched export.
                  </p>
                )}
              </div>
            )}

            {selectedProjectId && selectedProject && (
              <div className="studio-project-card">
                <label htmlFor="project-title" className="block text-xs font-medium mb-1">
                  Project title
                </label>
                <div className="flex gap-2">
                  <input
                    id="project-title"
                    value={renamingTitle}
                    onChange={(e) => setRenamingTitle(e.target.value)}
                    className="min-w-0 flex-1 rounded-lg border border-line bg-subtle px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-focus"
                  />
                  <button
                    type="button"
                    onClick={handleRenameProject}
                    className="rounded-lg border border-line px-3 py-1.5 text-sm font-medium text-content hover:bg-subtle"
                  >
                    Save
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => setProjectPendingDelete(selectedProject)}
                  disabled={exportingProjectId === selectedProject.id}
                  className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-danger-line px-3 py-2 text-sm font-medium text-danger hover:bg-danger-soft disabled:cursor-not-allowed disabled:opacity-50"
                  title="Delete project and keep its clips as unassigned videos"
                >
                  <Trash2 size={15} />
                  Delete project
                </button>
              </div>
            )}
          </aside>

          <section className="studio-library" aria-label="Storyboard">
            <VideoGallery
              jobs={selectedProjectJobs}
              project={selectedProject}
              loading={!jobsLoaded || projectsLoading}
              exporting={
                selectedProjectId !== null &&
                exportingProjectId === selectedProjectId
              }
              canExport={selectedProjectId !== null}
              onRetry={handleRetry}
              onExport={handleExportProject}
              onCreate={openCreate}
            />
          </section>

          <section ref={createRef} className="studio-create" aria-label="Create a video clip">
            <VideoConfigForm
              projectId={selectedProjectId}
              projectClipCount={selectedProjectJobs.length}
              onSubmit={handleSubmit}
              chatCredits={chatCredits}
              freeAccess={freeAccess}
              submitting={submitting}
              activeJobCount={activeJobCount}
              retryParams={retryParams}
            />
          </section>
      </main>

      <BuyChatCreditsModal
        isOpen={showBuyCredits}
        onClose={() => setShowBuyCredits(false)}
        chatCredits={chatCredits}
        uid={uid}
      />

      <Dialog open={!!projectPendingDelete} onClose={() => { if (!deletingProject) setProjectPendingDelete(null); }} title="Delete this project?">
        <p className="text-sm text-muted leading-7">Delete “{projectPendingDelete?.title}”? Your clips will be kept in Unassigned videos.</p>
        <div className="flex justify-end gap-3 mt-6"><button type="button" className="button-secondary" disabled={deletingProject} onClick={() => setProjectPendingDelete(null)}>Keep project</button><button type="button" className="button-primary !bg-danger-hover !border-danger-line" disabled={deletingProject} onClick={handleDeleteProject}>{deletingProject ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}Delete project</button></div>
      </Dialog>
    </div>
  );
};

export default VideoGenerator;
